<template>
  <div>
    <div
      :class="[
        $style.container,
        {
          [$style.hg]: !isIs24,
        },
      ]"
    >
      <h1 :class="$style.heading">{{ title }}</h1>
      <SearchBarLargeScreen />
      <MedianPrice />
      <PriceTrends />
      <PriceRoomDistribution />
      <PropertyValuation />
      <HgLoadWhenInViewport ref="featuredAgenciesRef" data-testid="featured-agencies">
        <template #default="{ hasBeenInViewportAtLeastOnce }">
          <FeaturedAgencies v-if="hasBeenInViewportAtLeastOnce" />
        </template>
      </HgLoadWhenInViewport>
      <InternalLink />
      <BreadCrumbs />
    </div>
    <component
      :is="isIs24 ? Is24Footer : HgFooter"
      data-testid="footer"
      :class="[
        $style.footer,
        {
          [$style.is24]: isIs24,
        },
      ]"
      :baseUrl="baseUrl"
      :currentLanguage="locale"
      :getLanguageToLink="getLanguageSwitchLinkFn"
      :collapsibleStartUnfolded="false"
    />
  </div>
</template>
<script setup lang="ts">
import {
  HgFooter,
  HgLoadWhenInViewport,
  HgSupportedLanguage,
  Is24Footer,
} from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { computed, defineAsyncComponent, nextTick, onMounted, type Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import BreadCrumbs from '@/components/breadCrumbs/BreadCrumbs.vue';
import FeaturedAgencies from '@/components/featured-agencies/FeaturedAgencies.vue';
import InternalLink from '@/components/internal-links/InternalLink.vue';
import MedianPrice from '@/components/median-price/MedianPrice.vue';
import PriceRoomDistribution from '@/components/price-room-distribution/PriceRoomDistribution.vue';
import PriceTrends from '@/components/price-trends/PriceTrends.vue';
import { SearchBarLargeScreen } from '@/components/search-bar';
import useDynamicTitle from '@/composables/useDynamicTitle';
import { isPlatformIs24 } from '@/helpers/platform';
import { getLanguageSwitchLink } from '@/router';
import useGeoStore from '@/stores/useGeoStore';

const isIs24 = isPlatformIs24();
const featuredAgenciesRef: Ref<typeof HgLoadWhenInViewport | null> = ref(null);
const emit = defineEmits<{
  (_e: 'onFeaturedAgenciesDemo'): void;
}>();

const PropertyValuation = defineAsyncComponent(() =>
  isIs24
    ? import('@/components/property-valuation/PropertyValuationIs24.vue')
    : import('@/components/property-valuation/PropertyValuationHg.vue'),
);

const { locale } = useI18n();
const route = useRoute();
const { getUrlByGeoLocation } = storeToRefs(useGeoStore());

const baseUrl: string = import.meta.env.VUE_PLATFORM_BASE_URL;
const getLanguageSwitchLinkFn = (language: HgSupportedLanguage) => {
  if (typeof route.meta?.getLanguageSwitchLink === 'function') {
    return route.meta.getLanguageSwitchLink(route, language, getUrlByGeoLocation.value[language]);
  }
  return getLanguageSwitchLink(route, language);
};
const { getDynamicTitle } = useDynamicTitle();
const title = computed(() => getDynamicTitle());

onMounted(async () => {
  const { demoCustomerId } = route.query;
  if (demoCustomerId) {
    const featuredAgenciesElement = featuredAgenciesRef.value?.$el;
    if (featuredAgenciesElement) {
      emit('onFeaturedAgenciesDemo');
      await nextTick();
      featuredAgenciesElement.scrollIntoView({ behavior: 'smooth' });
    }
  }
});
</script>

<style module lang="scss">
@use '@/styles/variables' as *;

.heading {
  margin-top: 0;
  margin-bottom: $space-100;

  @include heading-1;
}

.container {
  display: flex;
  flex-direction: column;

  > nav {
    order: -1;
  }

  > div:not(:last-child) {
    margin-bottom: $space-150;
  }

  &.hg {
    > div:nth-child(-n + 4) {
      &::after {
        display: block;
        width: 100%;
        height: 1px;
        margin: $space-150 0 auto 0;
        content: '';
        background: $color-grey-60;
      }

      @include grid-media-query(sm) {
        &::after {
          display: none;
        }
      }
    }

    > nav {
      order: unset;

      @include grid-media-query(sm) {
        order: -1;
      }
    }

    .heading {
      display: none;

      @include grid-media-query(sm) {
        display: block;
      }
    }
  }
}

.footer {
  margin-right: -$space-100;
  margin-left: -$space-100;

  [class*='col-sm-3'],
  [class*='col-lg-2'] {
    max-width: unset;
  }

  [class*='offset-lg-1'] {
    margin-left: unset;
  }

  [class*='_linkListGroups'] {
    flex-direction: column;
    justify-content: flex-end;
    margin-bottom: $space-150;

    @include grid-media-query(sm) {
      [class*='_collapsibleTrigger'] {
        pointer-events: auto !important;
      }

      [class*='_collapsibleIcon'],
      [class*='_iconCssClass'] {
        display: flex;
      }

      [class*='_collapsibleBody'] {
        border-bottom: 1px solid $color-black;
      }
    }
  }

  @include grid-media-query(sm) {
    [class*='_title'] {
      margin-bottom: $space-100;
    }
  }
}

.is24 {
  [class*='_linkListGroups'] {
    @include grid-media-query(sm) {
      [class*='_collapsibleBody'] {
        border-bottom: 1px solid $color-grey-50;
      }
    }
  }
}
</style>
